import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/authContext'
import { LoadingScreen } from '../ui/LoadingScreen'
import LoadErrorState from '../ui/LoadErrorState'

interface ProtectedRouteProps {
    children: React.ReactNode
}

/**
 * ProtectedRoute
 *
 * Guards pages that require authentication (dashboard, classroom, admin, etc.)
 *
 * States:
 * 1. !ready || isLoading  → show LoadingScreen (auth still initializing, don't redirect yet)
 * 2. ready && !user       → redirect to /login (confirmed: not logged in)
 * 3. ready && user        → render children (confirmed: logged in)
 *
 * The !ready guard eliminates the login-page flash on refresh.
 * Without it, user === null on mount causes an immediate redirect before
 * initAuth() has had a chance to restore the session from the refresh cookie.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { user, isLoading, ready, authOffline } = useAuth()
    const location = useLocation()
    const [showSkeleton, setShowSkeleton] = useState(false)

    useEffect(() => {
        if (ready) return
        const t = setTimeout(() => setShowSkeleton(true), 150)
        return () => clearTimeout(t)
    }, [ready])

    // Still initializing — show LoadingScreen only after 150ms (invisible on fast connections)
    if (!ready || isLoading) return showSkeleton ? <LoadingScreen /> : null

    // The profile check could not reach the server: the session is kept, so offer a retry
    // (reload re-runs the startup check) instead of sending a signed-in learner to /login.
    if (!user && authOffline) return <LoadErrorState kind="offline" />

    // Auth complete, not logged in — redirect to login
    if (!user) {
        return (
            <Navigate
                to="/login"
                replace
                state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }}
            />
        )
    }

    // Logged in — render the page
    return <>{children}</>
}

/**
 * PublicOnlyRoute
 *
 * Guards pages that logged-in users must not access (landing, login, signup, etc.)
 * Renders children immediately — no blank screen, no skeleton.
 *
 * Key behaviour: redirects to /dashboard ONLY if the user was already logged in
 * when auth first became ready (captured once via useEffect).
 * If the user logs in ON this page (e.g. handleEmailLogin, handleOAuthToken),
 * the page's own navigate() call handles the destination — we must NOT override it.
 *
 * States:
 * null  → auth still initializing; render children, do not redirect yet
 * true  → was logged in on arrival → redirect to /dashboard
 * false → was not logged in on arrival → render children; any subsequent login
 *         is navigated by the page itself, not this guard
 */
export const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, ready } = useAuth()

    // null = not yet determined | true = was logged in on arrival | false = was not
    const [initiallyLoggedIn, setInitiallyLoggedIn] = useState<boolean | null>(null)

    // React immediately retries this render, preserving the first resolved auth state.
    if (ready && initiallyLoggedIn === null) {
        setInitiallyLoggedIn(!!user)
    }

    // Was already logged in when they arrived → send to dashboard.
    if (initiallyLoggedIn) return <Navigate to="/dashboard" replace />

    // Auth still initializing (null) or confirmed not logged in on arrival (false):
    // render children. Login on this page is handled by the page's own navigate().
    return <>{children}</>
}
